import { useCallback, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import DualSliderInput from 'LuxKit/Slider/DualSliderInput'
import Group from 'components/utils/Group'
import debounce from 'lodash.debounce'
import { convertMinutesToTimeString } from 'lib/flights/flightUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'

interface Props {
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  selectedDepartureMaxItem?: string
  selectedDepartureMinItem?: string
  selectedArrivalMaxItem?: string
  selectedArrivalMinItem?: string
  departureAirportCode: string
  arrivalAirportCode: string
  departureTimeItems: Array<FilterPanelCheckItem>
  arrivalTimeItems: Array<FilterPanelCheckItem>
}

const STEP_NUM = 30

function FlightSingleFilterTime(props: Props) {
  const intl = useIntl()
  const {
    onFilterChange,
    selectedDepartureMinItem,
    selectedDepartureMaxItem,
    selectedArrivalMinItem,
    selectedArrivalMaxItem,
    departureAirportCode,
    arrivalAirportCode,
    departureTimeItems,
    arrivalTimeItems,
  } = props

  // Calculate min/max for departure times
  const departureMinNum = (departureTimeItems[0]?.value as number) || 0
  const departureMaxNum = (departureTimeItems[departureTimeItems.length - 1]?.value as number) || 1440
  const departureMin = Math.floor(departureMinNum / STEP_NUM) * STEP_NUM
  const rawDepartureMax = Math.ceil(departureMaxNum / STEP_NUM) * STEP_NUM
  // Ensure at least one step between min and max
  const departureMax = rawDepartureMax <= departureMin ? departureMin + STEP_NUM : rawDepartureMax

  // Calculate min/max for arrival times
  const arrivalMinNum = (arrivalTimeItems[0]?.value as number) || 0
  const arrivalMaxNum = (arrivalTimeItems[arrivalTimeItems.length - 1]?.value as number) || 1440
  const arrivalMin = Math.floor(arrivalMinNum / STEP_NUM) * STEP_NUM
  const rawArrivalMax = Math.ceil(arrivalMaxNum / STEP_NUM) * STEP_NUM
  // Ensure at least one step between min and max
  const arrivalMax = rawArrivalMax <= arrivalMin ? arrivalMin + STEP_NUM : rawArrivalMax

  const [departureStartValue, setDepartureStartValue] = useState(
    selectedDepartureMinItem ? parseInt(selectedDepartureMinItem) : departureMin,
  )
  const [departureEndValue, setDepartureEndValue] = useState(
    selectedDepartureMaxItem ? parseInt(selectedDepartureMaxItem) : departureMax,
  )
  const [arrivalStartValue, setArrivalStartValue] = useState(
    selectedArrivalMinItem ? parseInt(selectedArrivalMinItem) : arrivalMin,
  )
  const [arrivalEndValue, setArrivalEndValue] = useState(
    selectedArrivalMaxItem ? parseInt(selectedArrivalMaxItem) : arrivalMax,
  )

  // When we reset all the filters the value will be undefined so we need to set the value to min again
  useEffect(() => {
    if (!selectedDepartureMinItem) {
      setDepartureStartValue(departureMin)
    }
  }, [selectedDepartureMinItem, departureMin])

  // When we reset all the filters the value will be undefined so we need to set the value to max again
  useEffect(() => {
    if (!selectedDepartureMaxItem) {
      setDepartureEndValue(departureMax)
    }
  }, [selectedDepartureMaxItem, departureMax])

  // When we reset all the filters the value will be undefined so we need to set the value to min again
  useEffect(() => {
    if (!selectedArrivalMinItem) {
      setArrivalStartValue(arrivalMin)
    }
  }, [selectedArrivalMinItem, arrivalMin])

  // When we reset all the filters the value will be undefined so we need to set the value to max again
  useEffect(() => {
    if (!selectedArrivalMaxItem) {
      setArrivalEndValue(arrivalMax)
    }
  }, [selectedArrivalMaxItem, arrivalMax])

  const onChange = useMemo(() => {
    return debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange(e)
    }, 200)
  }, [onFilterChange])

  const handleDepartureStartChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDepartureStartValue(e.target.valueAsNumber)
    onChange(e)
  }, [onChange])

  const handleDepartureEndChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDepartureEndValue(e.target.valueAsNumber)
    onChange(e)
  }, [onChange])

  const handleArrivalStartChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setArrivalStartValue(e.target.valueAsNumber)
    onChange(e)
  }, [onChange])

  const handleArrivalEndChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setArrivalEndValue(e.target.valueAsNumber)
    onChange(e)
  }, [onChange])

  return (
    <Group direction="vertical" gap={16}>
      <Group direction="vertical" gap={8}>
        <BodyText variant="large">
          {intl.formatMessage(
            {
              id: 'kd-M2jGH353IKzikPC5G',
              defaultMessage: 'Departing from {airportCode}',
              description: 'Heading for the departure time range slider in the flight search filter panel, showing the airport code',
            },
            { airportCode: departureAirportCode },
          )}
        </BodyText>

        <DualSliderInput
          variant="label"
          name="minDepartureTime"
          min={departureMin}
          max={departureMax}
          step={STEP_NUM}
          startValue={departureStartValue}
          endValue={departureEndValue}
          onStartChange={handleDepartureStartChange}
          onEndChange={handleDepartureEndChange}
          format="custom"
          formatValue={convertMinutesToTimeString}
        />
      </Group>

      <Group direction="vertical" gap={8}>
        <BodyText variant="large">
          {intl.formatMessage(
            {
              id: 'nzILATCMPQ8wVse1kAi7',
              defaultMessage: 'Arrive at {airportCode}',
              description: 'Heading for the arrival time range slider in the flight search filter panel, showing the airport code',
            },
            { airportCode: arrivalAirportCode },
          )}
        </BodyText>

        <DualSliderInput
          variant="label"
          name="minArrivalTime"
          min={arrivalMin}
          max={arrivalMax}
          step={STEP_NUM}
          startValue={arrivalStartValue}
          endValue={arrivalEndValue}
          onStartChange={handleArrivalStartChange}
          onEndChange={handleArrivalEndChange}
          format="custom"
          formatValue={convertMinutesToTimeString}
        />
      </Group>
    </Group>
  )
}

export default FlightSingleFilterTime
